import type { BillingModel, PipelineType } from '@crm/contracts';
import { useQuery } from '@tanstack/react-query';
import type { FilterOption } from '../components/record-table/filter-bar.tsx';
import { api, type MemberView, type PipelineView, type ProjectRoleView } from './client.ts';
import { useSession } from './session.tsx';

/**
 * Shared reads the record pages lean on (RELAY-952): the organisation's pipelines (the same
 * cache entry Settings → Pipelines edits, so a renamed stage shows everywhere at once) and its
 * members (owners, recruiters, account managers).
 */
export const PIPELINES_QUERY = ['pipelines'] as const;
export const projectQuery = (id: string) => ['project', id] as const;
export const projectJobsQuery = (projectId: string) => ['project-jobs', projectId] as const;
export const BILLING_LABEL: Record<BillingModel, string> = {
  placement_fee: 'Placement fee',
  monthly: 'Monthly',
  hourly: 'Hourly',
};

/** The type's DEFAULT pipeline — the one a record's single stage column points into (TS-1745). */
export function usePipeline(type: PipelineType): PipelineView | undefined {
  const q = useQuery({ queryKey: PIPELINES_QUERY, queryFn: () => api.pipelines.list() });
  return q.data?.find((p) => p.appliesTo === type && p.isDefault);
}

/** Every live pipeline of the type, the default first (TS-1748: a project's bar per pipeline). */
export function usePipelines(type: PipelineType): PipelineView[] {
  const q = useQuery({ queryKey: PIPELINES_QUERY, queryFn: () => api.pipelines.list() });
  return (q.data ?? []).filter((p) => p.appliesTo === type);
}

/**
 * The options for a candidate's `stage`: the stage names of the default Candidate Flow pipeline,
 * which the API requires (`stage` mirrors the candidate's place there). A legacy value outside
 * them is kept as the first option so the current value still shows.
 */
export function candidateStageOptions(
  pipeline: PipelineView | undefined,
  current?: string | null,
): Array<{ value: string; label: string }> {
  const names = (pipeline?.stages ?? []).map((s) => s.name);
  const all = current && !names.includes(current) ? [current, ...names] : names;
  return all.map((name) => ({ value: name, label: name }));
}

export function useMembers(): { members: MemberView[]; nameOf: (id: string | null) => string } {
  const session = useSession();
  const q = useQuery({
    queryKey: ['users', session.me.organisation.id],
    queryFn: () => api.users(),
    staleTime: 60_000,
    // GET /users is user.read: a role without it gets no names, not a 403 on every page.
    enabled: session.can('user.read'),
  });
  const members = q.data ?? [];
  const known = q.isSuccess;
  return {
    members,
    nameOf: (id) => {
      if (!id) return '—';
      // Not read (no user.read, or the read failed): nobody is known, so never "Not a member".
      if (!known) return q.isFetching ? '…' : '—';
      const m = members.find((u) => u.id === id);
      return m?.name ?? m?.email ?? 'Not a current member';
    },
  };
}

/** The organisation's project functions (Settings → Projects), in order. */
export const projectRolesQuery = (org: string) => ['project-roles', org] as const;
export function useProjectRoles(enabled = true): ProjectRoleView[] {
  const session = useSession();
  const q = useQuery({
    queryKey: projectRolesQuery(session.me.organisation.id),
    queryFn: () => api.projectRoles.list(),
    staleTime: 60_000,
    enabled,
  });
  return q.data ?? [];
}

type Stage = PipelineView['stages'][number];
/** A stage as records carry it (`stage` on a project, deal, job or shortlist place). */
export const stageRefOf = (s: Stage) => ({
  id: s.id,
  name: s.name,
  slug: s.slug,
  colour: s.colour,
  isTerminal: s.isTerminal,
  terminalKind: s.terminalKind,
});
/** A pipeline's stages as filter-bar options (the value is the stage id the API filters on). */
/** A pipeline's stages as options, in pipeline order, grouped by outcome (open, won, lost). */
export const stageOptions = (p: PipelineView | undefined) =>
  p?.stages.map((s): FilterOption => ({
    value: s.id,
    label: s.name,
    group: s.terminalKind === 'won' || s.terminalKind === 'lost' ? s.terminalKind : 'open',
  })) ?? [];
/** Active members as filter-bar options. */
export const memberOptions = (members: MemberView[]) =>
  members
    .filter((m) => m.status === 'active')
    .map((m) => ({ value: m.id, label: m.name || m.email }));
